import { None } from "./types";
import { DictionaryProvider, DictionaryConsumer } from "./Dictionary";
import { Scalar, ScalarProvider, ScalarConsumer, scalarValue, onScalarValue } from "./Scalar";

export class Get<KeyType, ValueType>
	extends ScalarProvider<ValueType | None>
	implements
		ScalarConsumer<DictionaryProvider<KeyType, Scalar<ValueType>>>,
		DictionaryConsumer<KeyType, Scalar<ValueType>>
{
	private _subscriber: ScalarConsumer<ValueType | None> | undefined;
	private _key_subscription: ScalarConsumer<KeyType> | undefined;
	private _dictionary_source: DictionaryProvider<KeyType, ValueType> | undefined;
	private _value: Scalar<ValueType | None> | undefined;

	constructor(
		readonly scalar_source: Scalar<DictionaryProvider<KeyType, ValueType>>,
		readonly key_exp: Scalar<KeyType>,
		readonly __origin?: string
	) {
		super();
	}

	private ensure(value: Scalar<ValueType | None>) {
		if (this._value === value) return;
		if (this._value instanceof ScalarProvider) {
			this._value.unsubscribe(this._subscriber!);
		}
		this._value = value;
		if (this._value instanceof ScalarProvider) {
			this._value.subscribe(this._subscriber!);
		} else {
			this._subscriber!.update(this._value);
		}
	}

	get value() {
		if (this._subscriber) {
			return scalarValue(this._value!);
		} else {
			return scalarValue(scalarValue(this.scalar_source).entries.get(scalarValue(this.key_exp))) ?? None;
		}
	}

	update(value: DictionaryProvider<KeyType, ValueType>) {
		this._dictionary_source?.unsubscribe(this);
		this._dictionary_source = value;
		this._dictionary_source.subscribe(this);
		if (this._dictionary_source.entries.size === 0) {
			this.ensure(None);
		}
	}

	add(key: KeyType, value: Scalar<ValueType>) {
		if (key === scalarValue(this.key_exp)) {
			this.ensure(value);
		}
	}

	remove(key: KeyType) {
		if (key === scalarValue(this.key_exp)) {
			this.ensure(None);
		}
	}

	subscribe(subscriber: ScalarConsumer<ValueType | None>) {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		if (this.scalar_source instanceof ScalarProvider) {
			this.scalar_source.subscribe(this);
		} else {
			this.update(this.scalar_source);
		}
		onScalarValue(
			this.key_exp,
			(key_value) => this.ensure(this._dictionary_source!.entries.get(key_value) ?? None),
			(_, subscription) => (this._key_subscription = subscription)
		);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<ValueType | None>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		if (this._value instanceof ScalarProvider) {
			this._value.unsubscribe(subscriber);
		}
		if (this.scalar_source instanceof ScalarProvider) {
			this.scalar_source.unsubscribe(this);
		}
		this._dictionary_source!.unsubscribe(this);
		this._dictionary_source = undefined;
		if (this.key_exp instanceof ScalarProvider) {
			this.key_exp.unsubscribe(this._key_subscription!);
			this._key_subscription = undefined;
		}
	}
}
